import React, {
  type AllHTMLAttributes,
  type PropsWithChildren,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';

import useJoinedRef from '@mc/hooks/useJoinedRef';
import stylesheet from './GridLayout.less';

// Define IDS spacing tokens
export type SpacingToken =
  | 'space-0' // 0px
  | 'space-2' // 2px
  | 'space-4' // 4px
  | 'space-6' // 6px
  | 'space-8' // 8px
  | 'space-10' // 10px
  | 'space-12' // 12px
  | 'space-16' // 16px
  | 'space-20' // 20px
  | 'space-24' // 24px
  | 'space-32' // 32px
  | 'space-40' // 40px
  | 'space-60'; // 60px

// Define numeric gap type for backward compatibility
type NumericGap = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

export type GridLayoutProps = PropsWithChildren<{
  fit?: boolean;
  /**
   * The gap between grid items.
   * DEPRECATED - Numeric values (0-11) are deprecated. Please use spacing tokens (e.g. 'space-2', 'space-4', etc.) instead.
   * Numeric values will be removed in a future version.
   */
  gap?: NumericGap | SpacingToken;
  minColumnWidth?: string;
}> &
  AllHTMLAttributes<HTMLDivElement>;

/**
 * Given a container, `GridLayout` tries to fill it up with as many columns as
 * it can. No single column width can go under `minColumnWidth`. The number of
 * rows is fluid, dependent on the number of columns and the number of children.
 */
const GridLayout = React.forwardRef<HTMLDivElement, GridLayoutProps>(
  function GridLayout(
    {
      children,
      className,
      fit = true,
      gap = 2,
      minColumnWidth = '250px',
      style,
      ...props
    },
    forwardedRef,
  ) {
    const [isAboveMinColumnWidth, setIsAboveMinColumnWidth] = useState(false);
    const gridRef = useRef<HTMLDivElement>(null);
    const ref = useJoinedRef(forwardedRef, gridRef);

    const isSpacingFlagEnabled = mcdsFlagCheck(
      'xp_mcds_redesign_spacing_components',
    );
    // Remapping numeric gaps 9, 10, and 11 since larger spacing tokens are deprecating to reduce white space. Primitive IDS space tokens max at 60px.
    const effectiveGap = typeof gap === 'number' && gap >= 9 ? 'space-60' : gap;

    useLayoutEffect(() => {
      if (typeof ResizeObserver !== 'undefined') {
        const node = gridRef.current;

        // Check if node is null
        if (!node) {
          return;
        }

        // Create a proxy element to measure and convert the `minColumnWidth` value
        // (which might be em, rem, etc) to `px`

        const test = document.createElement('div');
        test.style.width = minColumnWidth;

        node.appendChild(test);
        const minToPixels = test.offsetWidth;

        node.removeChild(test);

        const observer = new ResizeObserver((entries) => {
          // We're only ever observing one element anyway.
          const entry = entries[0];
          const { width } = entry.contentRect;
          setIsAboveMinColumnWidth(width > minToPixels);
        });

        observer.observe(node);
        return function unobserve() {
          observer.unobserve(node);
        };
      }
    }, [minColumnWidth]);

    return (
      <div
        ref={ref}
        {...props}
        className={cx(
          stylesheet.root,
          typeof effectiveGap === 'number'
            ? stylesheet[`gap${effectiveGap}`]
            : isSpacingFlagEnabled && stylesheet[effectiveGap],
          className,
        )}
        style={{
          ...style,
          gridTemplateColumns: isAboveMinColumnWidth
            ? `repeat(${
                fit ? 'auto-fit' : 'auto-fill'
              }, minmax(${minColumnWidth}, 1fr))`
            : undefined,
        }}
      >
        {children}
      </div>
    );
  },
);

export default GridLayout;
